"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import { getCanonicalNextMintNumber, isMintingToday } from "@/components/meme-calendar/meme-calendar.helpers";
import { MEMES_CONTRACT } from "@/constants/constants";
import { shouldHideSubscriptions } from "@/components/user/layout/userPageVisibility";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import type { ApiUpcomingMemeSubscriptionStatus } from "@/generated/models/ApiUpcomingMemeSubscriptionStatus";
import type { NFTSubscription } from "@/generated/models/NFTSubscription";
import type { SubscriptionDetails } from "@/generated/models/SubscriptionDetails";
import useCapacitor from "@/hooks/useCapacitor";
import { commonApiFetch } from "@/services/api/common-api";
import { useQuery } from "@tanstack/react-query";
import { useContext, useMemo } from "react";
import MemeSubscriptionRow from "../../user/subscriptions/MemeSubscriptionRow";
function getProfileKey(connectedProfile: ApiIdentity | null): string | undefined {
  return connectedProfile?.consolidation_key ?? connectedProfile?.wallets?.map(wallet => wallet.wallet).join("-");
}
export default function LatestDropNextMintSubscribe(t0) {
  const $ = _c(41);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = t1;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    country
  } = useCookieConsent();
  const {
    isIos
  } = useCapacitor();
  const tokenId = getCanonicalNextMintNumber();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = Number.isInteger(tokenId) && tokenId > 0;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const hasTokenId = t2;
  let t3;
  if ($[3] !== country || $[4] !== isIos) {
    t3 = shouldHideSubscriptions({
      capacitorIsIos: isIos,
      country
    });
    $[3] = country;
    $[4] = isIos;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const hideSubscriptions = t3;
  let t4;
  if ($[6] !== activeProfileProxy || $[7] !== connectedProfile) {
    t4 = activeProfileProxy ? undefined : getProfileKey(connectedProfile);
    $[6] = activeProfileProxy;
    $[7] = connectedProfile;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const profileKey = t4;
  let t5;
  let t6;
  if ($[9] !== profileKey) {
    t5 = ["next-mint-subscription-details", profileKey];
    t6 = async () => await commonApiFetch({
      endpoint: `subscriptions/consolidation/details/${profileKey}`
    });
    $[9] = profileKey;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  const t7 = !hideSubscriptions && !!profileKey;
  let t8;
  if ($[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = {
      queryKey: t5,
      queryFn: t6,
      enabled: t7
    };
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const {
    data: details
  } = useQuery(t8);
  let t10;
  let t9;
  if ($[16] !== profileKey) {
    t9 = ["next-mint-subscription-status", profileKey, tokenId];
    t10 = async () => await commonApiFetch({
      endpoint: `subscriptions/consolidation/upcoming-memes/${tokenId}/${profileKey}`
    });
    $[16] = profileKey;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  const t11 = !hideSubscriptions && !!profileKey && hasTokenId;
  let t12;
  if ($[19] !== t10 || $[20] !== t11 || $[21] !== t9) {
    t12 = {
      queryKey: t9,
      queryFn: t10,
      enabled: t11
    };
    $[19] = t10;
    $[20] = t11;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const {
    data: status,
    refetch: refetchStatus
  } = useQuery(t12);
  let t13;
  bb0: {
    if (!profileKey || !hasTokenId || !status) {
      t13 = null;
      break bb0;
    }
    const t14 = status.count ?? 1;
    let t15;
    if ($[23] !== profileKey || $[24] !== status.subscribed || $[25] !== t14) {
      t15 = {
        consolidation_key: profileKey,
        contract: MEMES_CONTRACT,
        token_id: tokenId,
        subscribed: status.subscribed,
        subscribed_count: t14
      };
      $[23] = profileKey;
      $[24] = status.subscribed;
      $[25] = t14;
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    t13 = t15 as NFTSubscription;
  }
  const subscription = t13;
  let t14;
  if ($[27] !== details?.balance) {
    t14 = () => {
      const balance = details?.balance ?? 0;
      const safeBalance = Number.isFinite(balance) ? balance : 0;
      return new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 6
      }).format(Math.round(safeBalance * 1000000) / 1000000);
    };
    $[27] = details?.balance;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  details?.balance;
  let t15;
  if ($[29] !== t14) {
    t15 = t14();
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const balanceLabel = t15;
  if (hideSubscriptions || !profileKey || !subscription || props.showOnlyWhenSubscribed && !subscription.subscribed) {
    return null;
  }
  const t16 = details?.subscription_eligibility_count ?? status?.eligibility ?? 1;
  const t17 = props.readonly ?? false;
  let t18;
  if ($[31] !== refetchStatus) {
    t18 = () => {
      refetchStatus();
    };
    $[31] = refetchStatus;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = isMintingToday();
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== balanceLabel || $[35] !== profileKey || $[36] !== subscription || $[37] !== t16 || $[38] !== t17 || $[39] !== t18) {
    t20 = <div className="tw-mt-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-pt-4"><div className="tw-rounded-xl tw-bg-transparent"><MemeSubscriptionRow profileKey={profileKey} title="The Memes" subscription={subscription} eligibilityCount={t16} balanceLabel={balanceLabel} readonly={t17} refresh={t18} minting_today={t19} first={true} date={null} variant="compact" /></div></div>;
    $[34] = balanceLabel;
    $[35] = profileKey;
    $[36] = subscription;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  return t20;
}